import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Cross-page row selection for paginated tables.
 *
 * Twin of front-end/dashboard/src/hooks/useBulkSelection.ts — keep the two
 * identical (`diff` them) so the tables behave the same in both apps.
 *
 * Two modes, so "select all" never has to enumerate IDs the browser hasn't
 * loaded:
 *   - 'some' — an explicit set of clicked row ids
 *   - 'all'  — everything matching the current filters (across every page),
 *              tracked as the total count minus an `excluded` set
 *
 * The header checkbox flips between 'none' and 'all'. Un-ticking a row while in
 * 'all' mode just adds it to `excluded`. Selection persists across pagination
 * and is reset only when `resetKey` (a hash of the active filters) changes.
 */
export type BulkSelectionMode = 'none' | 'some' | 'all'

export interface UseBulkSelectionResult {
  mode: BulkSelectionMode
  /** Number of rows currently selected (cross-page total in 'all' mode). */
  count: number
  isSelected: (id: string) => boolean
  toggleRow: (id: string) => void
  /** Header checkbox: select everything matching, or clear. */
  toggleAll: () => void
  headerState: boolean | 'indeterminate'
  clear: () => void
  /** Explicit ids, when mode === 'some'. */
  selectedIds: string[]
  /** Excluded ids, when mode === 'all'. */
  excludedIds: string[]
}

export function useBulkSelection(opts: {
  totalCount: number
  /** Changing this clears the selection (e.g. when filters change). */
  resetKey?: string
}): UseBulkSelectionResult {
  const { totalCount, resetKey } = opts
  const [mode, setMode] = useState<BulkSelectionMode>('none')
  const [ids, setIds] = useState<Set<string>>(() => new Set())
  const [excluded, setExcluded] = useState<Set<string>>(() => new Set())

  const clear = useCallback(() => {
    setMode('none')
    setIds(new Set())
    setExcluded(new Set())
  }, [])

  // Reset when the underlying filter set changes — but not on first mount.
  const firstRun = useRef(true)
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false
      return
    }
    clear()
  }, [resetKey, clear])

  const isSelected = useCallback(
    (id: string) => (mode === 'all' ? !excluded.has(id) : ids.has(id)),
    [mode, excluded, ids],
  )

  const toggleRow = useCallback(
    (id: string) => {
      if (mode === 'all') {
        const next = new Set(excluded)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        // Everything de-selected one by one — fall back to no selection.
        if (next.size >= totalCount) {
          clear()
          return
        }
        setExcluded(next)
      } else {
        const next = new Set(ids)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        setIds(next)
        setMode(next.size === 0 ? 'none' : 'some')
      }
    },
    [mode, excluded, ids, totalCount, clear],
  )

  const toggleAll = useCallback(() => {
    if (mode === 'all' && excluded.size === 0) {
      clear()
    } else {
      setMode('all')
      setIds(new Set())
      setExcluded(new Set())
    }
  }, [mode, excluded, clear])

  const count =
    mode === 'none' ? 0 : mode === 'some' ? ids.size : Math.max(0, totalCount - excluded.size)

  const headerState: boolean | 'indeterminate' =
    mode === 'all' && excluded.size === 0 ? true : mode === 'none' ? false : 'indeterminate'

  return {
    mode,
    count,
    isSelected,
    toggleRow,
    toggleAll,
    headerState,
    clear,
    selectedIds: Array.from(ids),
    excludedIds: Array.from(excluded),
  }
}
